// Map a stored IDB chat payload (chatSerializer output) onto the chat-sync
// wire shape: one head patch + one render document per turn.
//
// The item→ordinal map is positional: each top-level node in `items[]` is one
// chat_turn at ordinal = its index. Turns are upsertable at any ordinal, so a
// re-sync of the same chat overwrites the same ordinals (a background
// sub-agent settling re-upserts its containing turn's ordinal).
//
// Turn payloads are RENDER documents, not continuation state: the pooled
// `snapshotRef` / `snapshot` (agent.state.messages at that turn) is stripped
// before shipping. A restored chat gets its agent messages back from the
// conversation store instead (`chatContinuation.js`), with the final assistant
// reply tail-filled out of these render documents - so what ships here is still
// only what the user saw. IDB (with the pool) stays the local source of truth
// in front of the sync layer.

// Upper bound on turns in one sync POST (matches the backend's
// MAX_TURNS_PER_SYNC). A chat with more turns than this is synced in
// successive POSTs (the engine chunks by this size); turns are upsertable at
// any ordinal, so no turn is ever dropped.
export const MAX_TURNS_PER_SYNC = 512;

// Largest decompressed SyncChat request the gateway accepts is 8 MiB; keep a
// batch's turn bytes under this with headroom for the head fields + JSON
// framing. Batching by turn count alone let a run of heavy turns (big frozen
// tool tables) bundle past the 8 MiB cap, get rejected, and park the chat as
// permanently-invalid — so heavy chats silently stopped backing up.
export const MAX_SYNC_BATCH_BYTES = 7 * 1024 * 1024;

// UTF-8 byte length, which is what every budget on this path is denominated in.
// `String.prototype.length` counts UTF-16 code units and undercounts by up to 3x
// on non-ASCII text, so a "compliant" body built against it can still exceed the
// server's byte-accurate cap -- a terminal 4xx that parks the chat.
const _batchEncoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;
export function wireByteLen(s) {
    return _batchEncoder ? _batchEncoder.encode(s).length : s.length;
}

// The wire bytes one turn contributes to a SyncChat body. buildSyncBody emits
// the payload/blob_refs as JSON *string* fields (payload_json/blob_refs_json),
// and postChatSync then JSON.stringify's the whole body — so the payload is
// escaped a SECOND time on the wire (every " and \ doubles). Measure that
// escaped form (double JSON.stringify) or a heavy turn's real size is
// undercounted by 15-30% and a "compliant" batch still blows the request cap.
export function turnWireBytes(turn) {
    return wireByteLen(JSON.stringify(JSON.stringify(turn.payload ?? {})))
        + wireByteLen(JSON.stringify(JSON.stringify(turn.blob_refs ?? [])));
}

// Pack turns into batches bounded by BOTH a turn count and a byte budget, so a
// batch never exceeds the server's request cap. Greedy: fill a batch until the
// next turn would overflow either bound, then start a new one. A single turn
// larger than `maxBytes` still goes out alone (never an empty batch) — the
// server rejects it and the engine parks it visibly, rather than the whole chat
// stalling silently. `firstBatchReserveBytes` shrinks the FIRST batch's budget
// to leave room for the head fields (canvas_json etc.) that ride POST #0. Pure +
// exported for unit testing.
export function chunkTurnsBySize(turns, maxTurns = MAX_TURNS_PER_SYNC, maxBytes = MAX_SYNC_BATCH_BYTES, firstBatchReserveBytes = 0) {
    const batches = [];
    let cur = [];
    let curBytes = 0;
    for (const t of turns) {
        const sz = turnWireBytes(t);
        const budget = batches.length === 0 ? Math.max(0, maxBytes - firstBatchReserveBytes) : maxBytes;
        if (cur.length > 0 && (cur.length >= maxTurns || curBytes + sz > budget)) {
            batches.push(cur);
            cur = [];
            curBytes = 0;
        }
        cur.push(t);
        curBytes += sz;
    }
    if (cur.length > 0) batches.push(cur);
    return batches;
}

// Approximate the head's wire byte cost (its stringified fields ride POST #0
// alongside the first turn batch). Used to reserve budget for it.
export function headWireBytes(head) {
    return wireByteLen(JSON.stringify(head ?? {}));
}

// Render-document shape version stamped on every synced turn. The server
// requires >= 1 and stores it opaquely for future render-doc migrations; bump
// this when the toRenderDocument shape changes incompatibly.
export const TURN_PAYLOAD_SCHEMA_VERSION = 1;

// Whether this chat's newest agent turn was cut off rather than finishing.
//
// `busyAtClose` alone is NOT sufficient, and relying on it is how the
// cross-device "stopped when you left" mark used to get lost: it is derived
// from the live `chat.busy` flag at serialize time, so it is already false by
// the time a reopened tab drains its outbox. The tab-close flush that would
// have carried it is best-effort and usually dies with the page, so on an
// abrupt close the mark was never recorded at all — and worse, the drain then
// sent `interrupted: false`, actively clearing any mark that HAD landed.
//
// The durable signal is in the items: `stripTransientFromItems` stamps
// `stopReason: "page_refresh"` on any turn still running when the record was
// serialized, and that survives into the persisted payload. Reading it back
// makes the mark recoverable after the fact.
//
// Newest TURN, not newest item: a chat whose last item is an unanswered user
// message is still in whatever state its last agent turn left it. A later
// clean run stamps its own stopReason, which is what clears the mark.
export function wasInterrupted(payload) {
    if (payload?.busyAtClose) return true;
    const items = payload?.items;
    if (!Array.isArray(items)) return false;
    for (let i = items.length - 1; i >= 0; i--) {
        if (items[i]?.kind !== "turn") continue;
        return items[i].stopReason === "page_refresh";
    }
    return false;
}

// "This chat holds something a server row would be for." Mirrors
// projectChatMeta's `hasItems` semantics (items or server turns) plus the
// canvas, which can be edited before any prompt. Canvas emptiness must be
// tested on canvas.items, not nullness — readCanvasStore returns a frozen
// `{items: []}` for an untouched chat, so every blank tab carries a
// non-null canvas. Used to withhold the CREATE sync for blank drafts;
// per-chat prefs (thinkingLevel/scopeMode) deliberately don't count, they
// ride the first real sync.
export function hasSyncableContent(payload) {
    if (!payload) return false;
    if (Array.isArray(payload.items) && payload.items.length > 0) return true;
    if ((payload.serverTurnCount ?? 0) > 0) return true;
    return Array.isArray(payload.canvas?.items) && payload.canvas.items.length > 0;
}

// The SyncChat head fields to set; absent fields leave the stored value
// unchanged, so we only include what the local record actually carries. Fields
// are FLAT on the request (no nested head object); the opaque render/UI JSON
// (`canvas`, `item_ui_state`) travels as JSON strings in the `*_json` fields.
export function buildChatHead(payload, { interrupted } = {}) {
    const head = {};
    if (payload.title != null) head.title = String(payload.title);
    // kind: null (normal) omits the field; "scout" sends it.
    if (payload.kind) head.kind = String(payload.kind);
    if (payload.thinkingLevel != null) head.thinking_level = String(payload.thinkingLevel);
    if (payload.scopeMode != null) head.scope_mode = String(payload.scopeMode);
    if (payload.canvas != null) head.canvas_json = JSON.stringify(payload.canvas);
    // item_ui_state = pure-presentation overlays (node expand/collapse).
    if (payload.toggled != null) head.item_ui_state_json = JSON.stringify(payload.toggled);
    // Tri-state on the wire: only send when we mean to set it. A normal
    // boundary leaves it unchanged; an exit flush passes interrupted=true, and
    // a clean run-end passes interrupted=false to clear a prior mark.
    if (typeof interrupted === "boolean") head.interrupted = interrupted;
    return head;
}

// Strip continuation state and any transient handles from one top-level item,
// leaving a render-only document. Recurses into children so a nested tool card
// is cleaned too. Kernel rebuild transcripts stay in IDB: they are not render
// content, and embedding the whole chat here can exceed the sync request limit.
function toRenderDocument(item) {
    if (!item || typeof item !== "object") {
        // The backend requires each turn payload to be a JSON object; wrap a
        // stray scalar under a namespaced key so it round-trips unambiguously —
        // a real render item shaped `{ value: x }` must not be mistaken for a
        // wrapper on the way back (assembleItemsFromTurns).
        return { [TURN_SCALAR_KEY]: item };
    }
    const { snapshot: _snap, snapshotRef: _ref, transcriptSnapshot: _transcript, abort: _abort, ...rest } = item;
    if (Array.isArray(rest.children)) {
        rest.children = rest.children.map(toRenderDocument);
    }
    return rest;
}

// Build the ordered turn documents for a payload's items — one per top-level
// item, at ordinal = its index. Returns ALL turns; the engine picks the changed
// subset (delta sync) and chunks them into MAX_TURNS_PER_SYNC-sized POSTs.
export function buildTurns(payload) {
    const items = Array.isArray(payload.items) ? payload.items : [];
    return items.map((item, ordinal) => ({
        turn_ordinal: ordinal,
        payload: toRenderDocument(item),
    }));
}

// cyrb53: a fast, well-distributed 53-bit non-cryptographic string hash. Used
// only for change-detection between two syncs of the same turn — not security.
// A false "changed" (hash differs for equal content) merely re-sends a turn
// (safe, wasteful); the length prefix in hashTurnPayload guards the dangerous
// direction (equal hash for changed content → a skipped send).
function cyrb53(str, seed = 0) {
    let h1 = 0xdeadbeef ^ seed;
    let h2 = 0x41c6ce57 ^ seed;
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

// Change-detection fingerprint of one turn's render document. Length-prefixed
// so a hash collision alone can't mask a real change.
export function hashTurnPayload(payload) {
    const s = JSON.stringify(payload) ?? "null";
    return `${s.length}:${cyrb53(s)}`;
}

// Fingerprint every turn of a stored payload → { turn_ordinal: hash }. Goes
// through buildTurns so the hashes match exactly what a subsequent sync computes
// (used to seed the outbox on restore so the first boundary doesn't re-send an
// unchanged transcript).
export function buildTurnHashes(payload) {
    const out = {};
    for (const t of buildTurns(payload)) out[t.turn_ordinal] = hashTurnPayload(t.payload);
    return out;
}

// Marker payload for a turn the client rewound away. The sync route is
// upsert-only (it never deletes turns beyond the sent set), so a rewind that
// shrinks items[] would otherwise leave the discarded tail live server-side and
// resurrect it on a fresh-device restore or conflict refetch. We instead
// overwrite those now-vacated ordinals with this tombstone, which the restore
// read (assembleItemsFromTurns) drops. `__chat_tombstone__` is namespaced so it
// can't collide with a real render item's fields.
export const TURN_TOMBSTONE_KEY = "__chat_tombstone__";

// Namespaced wrapper for a turn whose top-level item is a bare scalar (the
// backend requires each turn payload to be a JSON object). Explicit so the
// restore read never confuses a genuine single-key `{ value: … }` render item
// for a wrapped scalar.
export const TURN_SCALAR_KEY = "__chat_scalar__";

export function isTombstoneTurnPayload(payload) {
    return !!payload && typeof payload === "object" && payload[TURN_TOMBSTONE_KEY] === true;
}

function isScalarWrapper(payload) {
    return !!payload && typeof payload === "object"
        && Object.keys(payload).length === 1 && TURN_SCALAR_KEY in payload;
}

// Build tombstone turns for the ordinal range [fromOrdinal, toOrdinal]
// inclusive — the ordinals a shrink vacated that the server still holds.
export function buildTombstoneTurns(fromOrdinal, toOrdinal) {
    const out = [];
    for (let o = fromOrdinal; o <= toOrdinal; o++) {
        out.push({ turn_ordinal: o, payload: { [TURN_TOMBSTONE_KEY]: true } });
    }
    return out;
}

// Reverse: assemble a render items[] array from turns read back from PG.
// Turns may arrive in any order (the read path returns newest-first); sort by
// ordinal ascending so the transcript renders in sequence. Tombstoned turns
// (rewound-away tail) are dropped.
export function assembleItemsFromTurns(turns) {
    const rows = Array.isArray(turns) ? [...turns] : [];
    rows.sort((a, b) => (a.turn_ordinal ?? 0) - (b.turn_ordinal ?? 0));
    // Collapse any duplicate ordinal to its last occurrence before assembling.
    // The server holds exactly one row per ordinal (sync is upsert-by-ordinal),
    // so a duplicate can only come from a paginated read re-fetching a boundary
    // ordinal — the copies are identical, so last-wins is safe and keeps the
    // transcript from rendering a turn twice (there is no other ordinal dedup).
    const byOrdinal = new Map();
    for (const t of rows) byOrdinal.set(t.turn_ordinal ?? 0, t);
    return [...byOrdinal.values()]
        .filter((t) => !isTombstoneTurnPayload(t.payload))
        .map((t) => {
            const p = t.payload;
            if (isScalarWrapper(p)) return p[TURN_SCALAR_KEY];
            return p;
        });
}
